/* eslint-disable max-statements */

import { Box, Button, Center, Divider, Flex, Grid, Group, Stack, Text, Title } from '@mantine/core';
import moment from 'moment';
import { useMemo, useState } from 'react';

import {
  Batch,
  CalculationResult,
  CalculationResultTotal,
  Declaration,
  DeclarationCustomsType,
  DeclarationStatus,
  EnvironmentCountries,
  EORIGIN_DATE_FORMAT,
  NlAesMessageType,
  NlDmsMessageType,
} from '@e-origin/shared';

import { MultiLangCodes } from '@components';
import { useInvalidateOneHv, useInvalidateOneLv } from '@hooks/declaration';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { classes as globalClasses } from '@styles';
import { isHighValueDeclaration } from '@utils';
import { CommentsList } from '../../../../comments';
import { declarationModalManager } from '../../declarationModalManager';
import { Amendments } from './Amendment';
import { ControlNotification } from './ControlNotification';
import { ControlResultDetails, ControlResultDetailsDMS } from './ControlResultDetails';
import { CustomsStateNotifications } from './CustomsStateNotifications';
import { DeclarationViewCalculations } from './DeclarationViewCalculations';
import classes from './DeclarationViewCustoms.module.css';
import { DeclarationViewRejections } from './DeclarationViewRejections';
import { ExitControlResultNotification } from './ExitControlResult';
import { canDisplayInvalidationSection, canDisplayRequestInvalidationButton } from './invalidationEligibility';
import { ProofOfExitForm } from './ProofOfExitForm/ProofOfExitForm';

interface DeclarationDetailsCustomsProps {
  status?: string;
  declaration: Declaration;
}

interface CalculationsDisplayValues {
  items: CalculationResult[];
  total: CalculationResultTotal[];
}

interface CalculationsDisplay {
  duties: CalculationsDisplayValues;
  vat: CalculationsDisplayValues;
}

export const renderField = (label: string, value?: string | number, alwaysDisplay = false, dataPw?: string) =>
  (value || alwaysDisplay) && (
    <div data-pw={dataPw}>
      <label data-pw={dataPw ? `${dataPw}-label` : null}>{label}</label>
      <p data-pw={dataPw ? `${dataPw}-value` : null}>{value}</p>
    </div>
  );

export const DeclarationViewCustoms: React.FC<DeclarationDetailsCustomsProps> = ({ declaration, status }) => {
  const [invalidationRequestReason, setInvalidationRequestReason] = useState(declaration?.customsState?.invalidationRequest?.invalidationReason || '');
  const [invalidationMotivation, setInvalidationMotivation] = useState(declaration?.customsState?.invalidationRequest?.invalidationMotivation || '');

  const invalidateOneHvMutation = useInvalidateOneHv();
  const invalidateOneLvMutation = useInvalidateOneLv();

  const { data: settings } = useSettings();
  const isFR = settings?.country === EnvironmentCountries.FR;

  const codelistSystems = useCodeListSystem({ group: declaration.generalInfo.group, isExport: declaration.generalInfo.isExport });

  const handleInvalidationReasonChange = (e: any) => {
    setInvalidationRequestReason(e.target.value);
  };

  const isHighValue = isHighValueDeclaration(declaration.generalInfo.group);

  const isSendingInvalidationRequest = invalidateOneHvMutation.isPending || invalidateOneLvMutation.isPending;

  const isInvalidationRequestSent = !!declaration.customsState?.invalidationRequest?.invalidationRequestDate?.length;

  const isInvalidationRequestDisabled = !invalidationRequestReason.length || isSendingInvalidationRequest || (isFR && !invalidationMotivation);

  const handleSendInvalidationRequest = () => {
    if (isHighValue) {
      invalidateOneHvMutation.mutate({
        declaration,
        reason: invalidationRequestReason,
        invalidationMotivation: isFR ? invalidationMotivation : undefined,
      });
    } else {
      invalidateOneLvMutation.mutate({ declaration, reason: invalidationRequestReason });
    }
  };

  const hasCCRFIALog = useMemo(
    () => [NlDmsMessageType.CCRFIA as string, NlAesMessageType.CCRFIA].includes(declaration?.customsState?.customsLogs?.at(-1)?.customsMessageCode),
    [],
  );

  const showProofOfExit = useMemo(
    () =>
      settings?.country === EnvironmentCountries.NL &&
      declaration.generalInfo.isExport &&
      [DeclarationStatus.INVALIDATED, DeclarationStatus.EXPORTED, DeclarationStatus.RELEASED].includes(declaration.generalInfo.status),
    [declaration, settings],
  );

  const calculations: CalculationsDisplay = {
    duties: { items: [], total: [] },
    vat: { items: [], total: [] },
  };

  ['items', 'total'].forEach((type) => {
    declaration?.customsState?.calculationResults?.[type]?.forEach((item: any) => {
      if (item.taxType === 'A00') {
        calculations.duties[type].unshift(item);
        return;
      }
      calculations[item.taxType === 'B00' ? 'vat' : 'duties'][type].push(item);
    });
  });

  if (!declaration?.customsState) {
    return (
      <Center pt={140}>
        <Text>Not sent yet</Text>
      </Center>
    );
  }

  return (
    <Flex className={classes.container}>
      <Group className={classes.header}>
        {renderField('Status', status)}
        {renderField('Correlation ID', declaration.customsState?.corelationId || 'N/A', true, 'correlation-id')}

        {isFR && declaration.customsState?.customsRegistrationNumber && renderField('CRN', declaration.customsState?.customsRegistrationNumber)}

        {declaration.customsState?.movementReference && renderField('Movement reference number', declaration.customsState?.movementReference)}
        {declaration.customsState?.dateOfAcceptance && renderField('Date of acceptance', declaration.customsState?.dateOfAcceptance)}
        {declaration.customsState?.releaseForImport?.dateOfRelease && renderField('Date of release', declaration.customsState?.releaseForImport?.dateOfRelease)}
        {declaration.customsState?.noRelease?.rejectionDate && renderField('Rejection date', declaration.customsState?.noRelease?.rejectionDate)}
        {declaration.customsState?.noRelease?.rejectionMotivationText &&
          renderField('Decision reason', declaration.customsState?.noRelease?.rejectionMotivationText)}
        {isFR &&
          declaration.messageInfo?.presentationNotificationEstimatedDateTime &&
          renderField('Estimated arrival', moment(declaration.messageInfo?.presentationNotificationEstimatedDateTime).format(EORIGIN_DATE_FORMAT))}
        {declaration.generalInfo.status === DeclarationStatus.ACCEPTED &&
          declaration.customsState?.prelodge?.presentationNotificationDueDate &&
          renderField(
            'Presentation notification due date',
            moment(declaration.customsState?.prelodge?.presentationNotificationDueDate).format(EORIGIN_DATE_FORMAT),
          )}
      </Group>

      {declaration.customsState?.notifications && declaration.customsState?.notifications?.length > 0 && (
        <CustomsStateNotifications declaration={declaration} />
      )}

      {isHighValue && <CommentsList entityId={declaration._id} parentEntityId={(declaration?.messageInfo?.batch as unknown as Batch)?._id} />}

      <Amendments declaration={declaration} />
      <DeclarationViewCalculations calculations={calculations} declaration={declaration} />

      {declaration.customsState?.inControl && <ControlNotification declarationId={declaration._id} inControl={declaration.customsState?.inControl} />}

      {canDisplayInvalidationSection(declaration) && (
        <>
          <Divider mb="md" />

          <Title size="lg" fw="bold">
            Invalidation
          </Title>

          <Stack className={classes.invalidationInfoContainer} mt="md" mb="xl">
            {declaration.customsState?.invalidationRequest?.invalidationRequestDate !== undefined &&
              declaration.customsState?.invalidationRequest?.invalidationRequestDate.length > 0 && (
                <Group>
                  {renderField('Invalidation request date', declaration.customsState?.invalidationRequest?.invalidationRequestDate)}
                  {settings.country === EnvironmentCountries.NL &&
                    renderField(
                      'Invalidation initiated by customs',
                      declaration.customsState?.invalidationDecision?.invalidationInitiatedByCustoms === 1 ? 'yes' : 'no',
                    )}
                </Group>
              )}

            <Box className={classes.invalidationRequestForm} w="10%" miw={300}>
              {isFR && (
                <MultiLangCodes
                  value={invalidationMotivation}
                  label="Invalidation motivation"
                  code="RECORD_NIS_MOTIFS_INVALIDATION"
                  system={codelistSystems}
                  onChange={(value) => setInvalidationMotivation(value)}
                  disabled={isInvalidationRequestSent}
                />
              )}

              <label>Invalidation reason</label>

              <textarea
                className={globalClasses.inputBorder}
                value={invalidationRequestReason}
                onChange={handleInvalidationReasonChange}
                disabled={isInvalidationRequestSent}
              />

              {canDisplayRequestInvalidationButton(declaration, settings?.country) && (
                <div>
                  <Button
                    type="button"
                    disabled={isInvalidationRequestDisabled}
                    loading={isSendingInvalidationRequest}
                    onClick={handleSendInvalidationRequest}
                    variant="filled"
                    size="lg"
                  >
                    Request invalidation
                  </Button>
                </div>
              )}
            </Box>
          </Stack>
        </>
      )}

      <DeclarationViewRejections declaration={declaration} />

      {declaration.customsState?.sentTo === DeclarationCustomsType.NL_AES &&
        [DeclarationStatus.RELEASED, DeclarationStatus.NOT_RELEASED].includes(declaration.generalInfo?.status) && (
          <ControlResultDetailsDMS controlResult={declaration.customsState?.controlResult} />
        )}

      {declaration.generalInfo?.status === DeclarationStatus.NOT_RELEASED && declaration.customsState?.controlResult && (
        <>
          {declaration.customsState?.sentTo === DeclarationCustomsType.DMS ? (
            <ControlResultDetailsDMS controlResult={declaration.customsState?.controlResult} />
          ) : (
            <ControlResultDetails controlResult={declaration.customsState?.controlResult} />
          )}
        </>
      )}

      {[DeclarationCustomsType.DMS, DeclarationCustomsType.NL_AES].includes(declaration.customsState?.sentTo) && hasCCRFIALog && (
        <ControlResultDetailsDMS controlResult={declaration.customsState?.controlResult} showRfiTitle />
      )}

      {declaration.generalInfo?.status === DeclarationStatus.EXPORTED &&
        (declaration.customsReport?.exitControlResult || declaration.customsReport?.exitControlResults?.length) && (
          <ExitControlResultNotification
            exitControlResults={
              declaration.customsReport.exitControlResult ? [declaration.customsReport.exitControlResult] : (declaration.customsReport.exitControlResults ?? [])
            }
          />
        )}

      {(declaration.customsState?.customsSupervisingOffice?.referenceNumber || declaration.customsState?.customsOfficeOfExitActual?.referenceNumber) && (
        <>
          <Divider mb="md" />
          <Grid>
            {declaration.customsState?.customsSupervisingOffice?.referenceNumber && (
              <Grid.Col span={12}>
                <Stack className={classes.declarationOfficeSection}>
                  <Text className={classes.sectionTitle}>Declaration office</Text>
                  {renderField('Reference number', declaration.customsState.customsSupervisingOffice.referenceNumber)}
                </Stack>
              </Grid.Col>
            )}
            {declaration.customsState?.customsOfficeOfExitActual?.referenceNumber && (
              <Grid.Col span={12}>
                <Stack className={classes.declarationOfficeSection}>
                  <Text className={classes.sectionTitle}>Customs office of exit actual</Text>
                  {renderField('Reference number', declaration.customsState.customsOfficeOfExitActual.referenceNumber)}
                </Stack>
              </Grid.Col>
            )}
          </Grid>
        </>
      )}

      {hasCCRFIALog && (
        <Group mb="md">
          <Button
            type="button"
            onClick={() => declarationModalManager.openDuplicateAndAmendment(declaration, true)}
            variant="filled"
            size="lg"
            disabled={!!declaration.virtuals.isAmended}
          >
            Amend
          </Button>
        </Group>
      )}

      {showProofOfExit && <ProofOfExitForm declaration={declaration} />}
    </Flex>
  );
};
